import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/common/Button/Button';
import { Icon, icons } from '@/components/common/Icon';
import { NavDropdownItem } from '@/components/common/NavDropdownItem/NavDropdownItem';
import type { LucideIcon } from 'lucide-react';
import Image from '@/components/common/Image';

interface NavLink {
  title: string;
  subtitle: string;
  href: string;
  icon: LucideIcon;
}

interface NavSection {
  title: string;
  links: NavLink[];
}

const developerDocsLinks: Record<string, NavLink[]> = {
  gettingStarted: [
    {
      title: 'Getting Started',
      subtitle: 'The first step with Cobalt',
      href: 'https://docs.gocobalt.io/overview',
      icon: icons.fileText,
    },
    {
      title: 'Implementation Guide',
      subtitle: 'Learn how to implement Cobalt',
      href: 'https://docs.gocobalt.io/implementation/overview',
      icon: icons.code,
    },
    {
      title: 'Deployment',
      subtitle: 'Deploy your integrations',
      href: 'https://docs.gocobalt.io/deployment/overview',
      icon: icons.zap,
    },
  ],
  reference: [
    {
      title: 'API Reference',
      subtitle: 'Complete API documentation',
      href: 'https://docs.gocobalt.io/api-reference/overview',
      icon: icons.terminal,
    },
    {
      title: 'SDKs',
      subtitle: 'Official SDK libraries',
      href: 'https://docs.gocobalt.io/sdks/overview',
      icon: icons.code,
    },
    {
      title: 'Connector Guides',
      subtitle: 'Integration connector docs',
      href: 'https://docs.gocobalt.io/resources/integration-providers/overview',
      icon: icons.link,
    },
  ],
  examples: [
    {
      title: 'Build Salesforce Integration',
      subtitle: 'Connect with Salesforce',
      href: 'https://docs.gocobalt.io/implementation/examples/build_salesforce',
      icon: icons.cloud,
    },
    {
      title: 'Build NetSuite Integration',
      subtitle: 'Connect with NetSuite',
      href: 'https://docs.gocobalt.io/implementation/examples/build_netsuite',
      icon: icons.database,
    },
    {
      title: 'Build Workday Integration',
      subtitle: 'Connect with Workday',
      href: 'https://docs.gocobalt.io/implementation/examples/build_workday',
      icon: icons.briefcase,
    },
  ],
};

const resourceSections: NavSection[] = [
  {
    title: 'Resources',
    links: [
      {
        title: 'Case Studies',
        subtitle: 'Customer success stories',
        href: 'https://gocobalt.io/case-studies/',
        icon: icons.fileText,
      },
      {
        title: 'Blog',
        subtitle: 'Latest news and articles',
        href: 'https://gocobalt.io/blog/',
        icon: icons.edit,
      },
      {
        title: 'About Us',
        subtitle: 'Our story and mission',
        href: '/about-us',
        icon: icons.users,
      },
      {
        title: 'Careers',
        subtitle: 'Join our team',
        href: '/careers',
        icon: icons.briefcase,
      },
    ],
  },
  {
    title: 'Security',
    links: [
      {
        title: 'Security',
        subtitle: 'Our security practices',
        href: '/security',
        icon: icons.lock,
      },
    ],
  },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const docsDropdownRef = useRef<HTMLDivElement>(null);
  const resourcesDropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<number | null>(null);
  const navigate = useNavigate();

  const handleClickOutside = useCallback((event: MouseEvent) => {
    // Check if click is outside dropdowns and mobile menu
    if (
      (docsDropdownRef.current && !docsDropdownRef.current.contains(event.target as Node)) &&
      (resourcesDropdownRef.current && !resourcesDropdownRef.current.contains(event.target as Node)) &&
      (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node))
    ) {
      setActiveDropdown(null);
    }
  }, []);

  const handleNavigation = useCallback((path: string) => {
    window.scrollTo(0, 0);
    navigate(path);
    setIsMenuOpen(false);  // Close mobile menu when navigating
    setActiveDropdown(null); // Close any active dropdowns
  }, [navigate]);

  const handleExternalNavigation = useCallback((url: string) => {
    // If the URL is absolute (starts with http), open in a new tab
    if (url.startsWith('http')) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      // For relative URLs, use the navigate function
      handleNavigation(url);
    }
    setIsMenuOpen(false);  // Close mobile menu when navigating
    setActiveDropdown(null); // Close any active dropdowns
  }, [handleNavigation]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    };
  }, [handleClickOutside]);

  const toggleMenu = useCallback(() => {
    setIsMenuOpen((prev) => !prev);
    // Reset active dropdown when toggling menu
    setActiveDropdown(null);
  }, []);

  const handleMouseEnter = useCallback((dropdown: string) => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveDropdown(dropdown);
  }, []);

  const handleMouseLeave = useCallback(() => {
    closeTimeoutRef.current = window.setTimeout(() => {
      setActiveDropdown(null);
    }, 300); // 300ms delay before closing
  }, []);

  const toggleDropdown = useCallback((dropdown: string) => {
    setActiveDropdown((prev) => (prev === dropdown ? null : dropdown));
  }, []);

  // Helper function to handle link clicks in mobile dropdowns
  const handleLinkClick = useCallback((href: string) => {
    if (href.startsWith('http')) {
      handleExternalNavigation(href);
    } else {
      handleNavigation(href);
    }
  }, [handleExternalNavigation, handleNavigation]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div onClick={() => handleNavigation('/')} className="flex items-center cursor-pointer">
            <Image src="/logo.png" alt="Cobalt Logo" width={150} height={150} />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <div className="relative flex items-center">
              <span onClick={() => handleNavigation('/cobalt-mcp')} className="text-gray-600 hover:text-gray-900 cursor-pointer">
                Cobalt MCP
              </span>
              <span className="ml-2 inline-flex items-center rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-600">
                New
              </span>
            </div>

            <span onClick={() => handleNavigation('/integrations')} className="text-gray-600 hover:text-gray-900 cursor-pointer">
              Integrations
            </span>

            {/* Developer Docs Dropdown */}
            <div
              className="relative"
              ref={docsDropdownRef}
              onMouseEnter={() => handleMouseEnter('docs')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                type="button"
                className="flex items-center text-gray-600 hover:text-gray-900"
                onClick={() => toggleDropdown('docs')}
                aria-expanded={activeDropdown === 'docs'}
              >
                <span>Developer Docs</span>
                <Icon
                  icon={icons.chevronDown}
                  className="ml-1 w-4 h-4 transition-transform duration-200"
                  style={{
                    transform: activeDropdown === 'docs' ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                />
              </button>

              {activeDropdown === 'docs' && (
                <div
                  className="absolute top-full left-0 w-[800px] p-6 bg-white rounded-lg shadow-lg border border-gray-200 grid grid-cols-3 gap-6 mt-1"
                  onMouseEnter={() => handleMouseEnter('docs')}
                  onMouseLeave={handleMouseLeave}
                >
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Getting Started</h3>
                    <div className="space-y-2">
                      {developerDocsLinks.gettingStarted.map((link) => (
                        <div key={link.href} onClick={() => handleLinkClick(link.href)} className="cursor-pointer">
                          <NavDropdownItem {...link} />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Reference</h3>
                    <div className="space-y-2">
                      {developerDocsLinks.reference.map((link) => (
                        <div key={link.href} onClick={() => handleLinkClick(link.href)} className="cursor-pointer">
                          <NavDropdownItem {...link} />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Common Examples</h3>
                    <div className="space-y-2">
                      {developerDocsLinks.examples.map((link) => (
                        <div key={link.href} onClick={() => handleLinkClick(link.href)} className="cursor-pointer">
                          <NavDropdownItem {...link} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Resources Dropdown */}
            <div
              className="relative"
              ref={resourcesDropdownRef}
              onMouseEnter={() => handleMouseEnter('resources')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                type="button"
                className="flex items-center text-gray-600 hover:text-gray-900"
                onClick={() => toggleDropdown('resources')}
                aria-expanded={activeDropdown === 'resources'}
              >
                <span>Resources</span>
                <Icon
                  icon={icons.chevronDown}
                  className="ml-1 w-4 h-4 transition-transform duration-200"
                  style={{
                    transform: activeDropdown === 'resources' ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                />
              </button>

              {activeDropdown === 'resources' && (
                <div
                  className="absolute top-full left-0 w-[600px] p-6 bg-white rounded-lg shadow-lg border border-gray-200 grid grid-cols-2 gap-6 mt-1"
                  onMouseEnter={() => handleMouseEnter('resources')}
                  onMouseLeave={handleMouseLeave}
                >
                  {resourceSections.map((section) => (
                    <div key={section.title}>
                      <h3 className="font-semibold text-gray-900 mb-3">{section.title}</h3>
                      <div className="space-y-2">
                        {section.links.map((link) => (
                          <div key={link.href} onClick={() => handleLinkClick(link.href)} className="cursor-pointer">
                            <NavDropdownItem {...link} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <span onClick={() => handleNavigation('/pricing')} className="text-gray-600 hover:text-gray-900 cursor-pointer">
              Pricing
            </span>
          </nav>

          {/* Desktop Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button variant="gradient" color="primary" onClick={() => handleExternalNavigation('https://cal.com/cobalt/welcome-to-cobalt')}>
              Book a Demo
            </Button>
            <Button variant="solid" color="primary" onClick={() => handleExternalNavigation('/signup')}>
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            onClick={toggleMenu}
            aria-expanded={isMenuOpen}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            <Icon
              icon={isMenuOpen ? icons.close : icons.menu}
              className="h-6 w-6"
            />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div ref={mobileMenuRef} className="lg:hidden py-4">
            <nav className="flex flex-col space-y-4">
              <span
                onClick={() => {
                  handleNavigation('/cobalt-mcp');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md cursor-pointer"
              >
                <div className="flex items-center">
                  <span>Cobalt MCP</span>
                  <span className="ml-2 inline-flex items-center rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-600">
                    New
                  </span>
                </div>
              </span>

              <span
                onClick={() => {
                  handleNavigation('/integrations');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md cursor-pointer"
              >
                Integrations
              </span>

              {/* Mobile Developer Docs */}
              <div className="px-4 py-2">
                <button
                  type="button"
                  className="flex items-center w-full text-left text-gray-600 hover:text-gray-900"
                  onClick={() => toggleDropdown('docs')}
                >
                  <span>Developer Docs</span>
                  <Icon
                    icon={icons.chevronDown}
                    className="ml-auto w-4 h-4 transition-transform duration-200"
                    style={{
                      transform: activeDropdown === 'docs' ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}
                  />
                </button>

                {activeDropdown === 'docs' && (
                  <div className="mt-2 space-y-4 pl-4">
                    {/* Getting Started */}
                    <div className="mb-3">
                      <h3 className="font-semibold text-gray-900 mb-2">Getting Started</h3>
                      <div className="space-y-2">
                        {developerDocsLinks.gettingStarted.map((link) => (
                          <div key={link.href} onClick={() => handleLinkClick(link.href)} className="cursor-pointer">
                            <NavDropdownItem {...link} />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Reference */}
                    <div className="mb-3">
                      <h3 className="font-semibold text-gray-900 mb-2">Reference</h3>
                      <div className="space-y-2">
                        {developerDocsLinks.reference.map((link) => (
                          <div key={link.href} onClick={() => handleLinkClick(link.href)} className="cursor-pointer">
                            <NavDropdownItem {...link} />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Examples */}
                    <div className="mb-3">
                      <h3 className="font-semibold text-gray-900 mb-2">Common Examples</h3>
                      <div className="space-y-2">
                        {developerDocsLinks.examples.map((link) => (
                          <div key={link.href} onClick={() => handleLinkClick(link.href)} className="cursor-pointer">
                            <NavDropdownItem {...link} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Resources */}
              <div className="px-4 py-2">
                <button
                  type="button"
                  className="flex items-center w-full text-left text-gray-600 hover:text-gray-900"
                  onClick={() => toggleDropdown('resources')}
                >
                  <span>Resources</span>
                  <Icon
                    icon={icons.chevronDown}
                    className="ml-auto w-4 h-4 transition-transform duration-200"
                    style={{
                      transform: activeDropdown === 'resources' ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}
                  />
                </button>

                {activeDropdown === 'resources' && (
                  <div className="mt-2 space-y-4 pl-4">
                    {resourceSections.map((section) => (
                      <div key={section.title} className="mb-3">
                        <h3 className="font-semibold text-gray-900 mb-2">{section.title}</h3>
                        <div className="space-y-2">
                          {section.links.map((link) => (
                            <div key={link.href} onClick={() => handleLinkClick(link.href)} className="cursor-pointer">
                              <NavDropdownItem {...link} />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <span
                onClick={() => {
                  handleNavigation('/pricing');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md cursor-pointer"
              >
                Pricing
              </span>
              <div className="flex flex-col space-y-4 px-4 pt-4 border-t border-gray-200">
                <Button
                  variant="gradient"
                  color="primary"
                  className="w-full"
                  onClick={() => handleExternalNavigation('https://cal.com/cobalt/welcome-to-cobalt')}
                >
                  Book a Demo
                </Button>
                <Button
                  variant="solid"
                  color="primary"
                  className="w-full"
                  onClick={() => handleExternalNavigation('/signup')}
                >
                  Get Started
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;